import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environment';
import { tap, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ErrorHandlerService } from './error-handler.service';
import { getJwtExpiration } from '../models/utils';
import { MatDialog } from '@angular/material/dialog';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly tokenKey = 'token';
  private readonly localStorageKeys = [this.tokenKey, 'username', 'userRole'];
  apiUrl = environment.apiUrl;
  private loginUrl = this.apiUrl + '/v1/auth/signin';
  private saveUserUrl = this.apiUrl + '/v1/auth/signup';
  private getUserUrl = this.apiUrl + '/v1/auth/signup';
  private putUserUrl = this.apiUrl + '/v1/auth/signup';
  private logoutTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastr: ToastrService,
    private errorHandler: ErrorHandlerService,
    private dialog: MatDialog,
  ) {
    this.restoreSession();

    window.addEventListener('storage', event => {
      if (event.key !== this.tokenKey) return;

      if (event.newValue) {
        this.restoreSession();
      } else {
        this.endSession(false);
      }
    });

    // Browsers can pause timers while a tab or device is suspended. Validate the
    // absolute JWT expiration again as soon as the application becomes active.
    window.addEventListener('focus', () => this.validateCurrentSession());
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.validateCurrentSession();
    });
  }

  login(username: string, password: string) {
    return this.http.post(this.loginUrl, { login: username, password: password }).pipe(
      tap((response: any) => {
        // Guardar el token en el localStorage
        localStorage.setItem(this.tokenKey, response.accessToken);
        localStorage.setItem('username', response.username);
        localStorage.setItem('userRole', response.userRole);

        // Iniciar el temporizador de logout
        this.startLogoutTimer();
      }),
      catchError((error: HttpErrorResponse) => {
        // Manejar el error aquí
        console.error('Error al autenticar:', error);
        return throwError(error);
      }),
    );
  }

  updateUserEnabled(id: number, status: boolean): Observable<void> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    });
    return this.http.delete<void>(`${this.putUserUrl}/${id}/${status}`, { headers });
  }

  saveUser(myForm: any): Observable<any> {
    const params: { [key: string]: any } = this.getUserParams(myForm);
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }),
    };
    return this.http.post(this.saveUserUrl, params, httpOptions).pipe(
      tap((response: any) => {}),
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.handleError(error);
        return throwError(error.error);
      }),
    );
  }
  getUserByFilters(page: number, size: number, status: boolean, search: string) {
    const params: { [key: string]: any } = {
      page: page,
      size: size,
      search: search,
    };
    if (status) {
      params['status'] = true;
    } else {
      params['status'] = false;
    }

    const httpOptions = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      params: params,
    };
    return this.http.get(this.getUserUrl, httpOptions).pipe(
      tap((response: any) => {}),
      catchError((error: HttpErrorResponse) => {
        this.errorHandler.handleError(error);
        return throwError(error.error);
      }),
    );
  }

  getUserParams(myForm: any) {
    const params: { [key: string]: any } = {
      login: myForm.name,
      password: myForm.password,
      role: myForm.rol,
    };
    return params;
  }

  isLoggedIn(): boolean {
    const token = localStorage.getItem(this.tokenKey);
    if (!token) return false;

    const expiration = getJwtExpiration(token);
    return expiration !== null && expiration > Date.now();
  }

  hasStoredToken(): boolean {
    return localStorage.getItem(this.tokenKey) !== null;
  }

  logout(): void {
    this.endSession(false);
  }

  /** Clears expired or invalid authentication state without leaving protected UI visible. */
  expireSession(redirectToLogin = true): void {
    this.endSession(true, redirectToLogin);
  }

  getUserRole() {
    return localStorage.getItem('userRole');
  }

  startLogoutTimer(): void {
    this.clearLogoutTimer();

    const token = localStorage.getItem(this.tokenKey);
    const expiration = token ? getJwtExpiration(token) : null;
    if (expiration === null || expiration <= Date.now()) {
      this.expireSession();
      return;
    }

    this.logoutTimer = setTimeout(() => this.expireSession(), expiration - Date.now());
  }

  private restoreSession(): void {
    const token = localStorage.getItem(this.tokenKey);
    if (!token) return;

    const expiration = getJwtExpiration(token);
    if (expiration === null || expiration <= Date.now()) {
      // Initial navigation is owned by AuthGuard. The constructor only restores
      // or clears persisted state so it cannot race Angular's initial routing.
      this.clearLogoutTimer();
      this.clearStoredSession();
      return;
    }

    this.startLogoutTimer();
  }

  private validateCurrentSession(): boolean {
    if (!this.hasStoredToken()) return false;

    const isLoggedIn = this.isLoggedIn();
    if (!isLoggedIn) {
      this.expireSession();
    }

    return isLoggedIn;
  }

  private endSession(expired: boolean, redirectToLogin = true): void {
    const hadSession = this.localStorageKeys.some(key => localStorage.getItem(key) !== null);
    this.clearLogoutTimer();
    this.clearStoredSession();
    this.dialog.closeAll();

    if (expired && hadSession) {
      this.toastr.error('La sesión ha expirado. Inicia sesión nuevamente.', 'Sesión expirada');
    }

    if (redirectToLogin && this.router.url !== '/login') {
      void this.router.navigateByUrl('/login', { replaceUrl: true });
    }
  }

  private clearStoredSession(): void {
    this.localStorageKeys.forEach(key => localStorage.removeItem(key));
  }

  private clearLogoutTimer(): void {
    if (this.logoutTimer !== undefined) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = undefined;
    }
  }
}
